/**
 * AgentNodeToolbar -- Floating pill-shaped toolbar for agent nodes
 *
 * Positioned absolutely above the node. Uses inverse zoom scaling
 * to maintain readable size when zoomed out.
 * Springs up with fade-in animation when node is selected.
 */

import { memo, useCallback, useEffect, useId, useRef, useState, type FocusEvent, type Ref } from 'react'
import { useNodeId, useStore } from 'reactflow'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronDown } from 'lucide-react'
import { cn } from '../../../../lib/utils'
import { createNadDebug } from '../../../../utils/debugLog'
import { useCanShowSingleNodeDetails } from './SingleNodeDetailsContext'
import { getViewportZoom } from './viewportZoom'
import { useNodePresentation } from '../../NodePresentationContext'
import { pickedOutputIndex, trackNodeToolbarAction } from '../../../../analytics/interactionAnalytics'
import { viewModeForPresentation } from '../../../../analytics/assetDownloadAnalytics'
import { useOptionalProjectId } from '../../../../contexts/ProjectIdContext'
import { useNuxTooltipEvents } from '../../../../onboarding/nuxEvents'

export const FLOATING_NODE_TOOLBAR_LABEL_CLEARANCE_PX = 36
/** Grace period so the pointer can travel from a trigger into its dropdown. */
export const TOOLBAR_MENU_HOVER_CLOSE_DELAY_MS = 150

const toolbarPositionLog = createNadDebug('floating-node-toolbar')
const toolbarMenuLog = createNadDebug('dw:toolbar-menu')
const locationToolbarReferenceLog = createNadDebug('dw:location-360-toolbar')

interface ToolbarActionBase {
  key: string
  label: string
  icon: React.ReactNode
  variant?: 'primary' | 'default' | 'danger'
  disabled?: boolean
  /**
   * Why the action is disabled. Rendered in the hover tooltip (and exposed via
   * ``aria-describedby``) only while ``disabled`` is true, so a greyed-out
   * button explains itself instead of silently doing nothing.
   */
  disabledReason?: string
  hidden?: boolean
  /** Group separator -- renders a divider before this action */
  group?: boolean
  /** Hide from toolbar (show elsewhere, e.g., footer) */
  hideFromToolbar?: boolean
  /** Optional anchor used by portal-based flyovers opened from this action. */
  triggerRef?: Ref<HTMLButtonElement>
  /** Accessibility state for non-menu popovers such as configuration dialogs. */
  ariaHasPopup?: 'dialog' | 'menu'
  ariaExpanded?: boolean
  ariaControls?: string
  nuxAnchorId?: string
  nuxControlId?: string
}

export interface ToolbarMenuItem {
  key: string
  label: string
  icon?: React.ReactNode
  onClick: () => void
  disabled?: boolean
  hidden?: boolean
}

export type ToolbarAction =
  | (ToolbarActionBase & {
      onClick: () => void
      menuItems?: never
    })
  | (ToolbarActionBase & {
      onClick?: never
      menuItems: ToolbarMenuItem[]
    })

interface AgentNodeToolbarProps {
  actions: ToolbarAction[]
  isVisible: boolean
}

export const AgentNodeToolbar = memo(function AgentNodeToolbar({
  actions,
  isVisible,
}: AgentNodeToolbarProps) {
  const canShowSingleNodeDetails = useCanShowSingleNodeDetails()
  const visibleActions = actions.filter(
    (action) =>
      !action.hidden &&
      !action.hideFromToolbar &&
      (!action.menuItems || action.menuItems.some((item) => !item.hidden)),
  )
  const shouldShowToolbar = isVisible && canShowSingleNodeDetails

  return (
    <AnimatePresence>
      {shouldShowToolbar && visibleActions.length > 0 && (
        <VisibleAgentNodeToolbar
          key="agent-node-toolbar"
          actions={visibleActions}
        />
      )}
    </AnimatePresence>
  )
})

const VisibleAgentNodeToolbar = memo(function VisibleAgentNodeToolbar({
  actions,
}: {
  actions: ToolbarAction[]
}) {
  const presentation = useNodePresentation()
  const nodeId = useNodeId()
  const nodeType = useStore((s) => (nodeId ? s.nodeInternals.get(nodeId)?.type : undefined))
  const projectId = useOptionalProjectId()
  const inline = presentation === 'detail'
  const canvasZoom = useStore(getViewportZoom)
  const zoom = inline ? 1 : canvasZoom
  const visibleActionCount = actions.length
  const scale = Math.min(Math.max(1 / zoom, 0.5), 2)
  const referenceSignatureRef = useRef<string | null>(null)

  useEffect(() => {
    toolbarPositionLog({
      event: 'toolbar-positioned-above-label',
      feature: 'floating-node-toolbar',
      trace_id: `floating-node-toolbar:${nodeId ?? 'detail'}`,
      node_id: nodeId ?? undefined,
      node_type: nodeType,
      clearance_px: FLOATING_NODE_TOOLBAR_LABEL_CLEARANCE_PX,
      action_count: visibleActionCount,
      placement: 'top',
      zoom,
      scale,
    })
  }, [nodeId, nodeType, scale, visibleActionCount, zoom])

  useEffect(() => {
    if (nodeType !== 'locationDesigner') return
    const actionIds = actions.map((action) => action.key)
    const layoutVariant = actionIds.includes('panorama-reset') ? 'location_360' : 'regular'
    const traceActionAliases: Record<string, string> = {
      addToChat: 'chat',
      'create-panorama': 'create-360',
      'panorama-reset': 'reset',
      'panorama-extract': 'extract',
      'save-to-library': 'library',
      'bg-color': 'color',
    }
    const traceActionNames = actionIds.map((actionId) => traceActionAliases[actionId] ?? actionId)
    const signature = actionIds.join('\u0000')
    if (referenceSignatureRef.current === signature) return
    referenceSignatureRef.current = signature
    locationToolbarReferenceLog({
      event: 'location-toolbar-layout-rendered',
      feature: 'location-360-toolbar',
      trace_id: `location-360-toolbar:${nodeId ?? 'detail'}`,
      project_id: projectId ?? undefined,
      node_id: nodeId ?? undefined,
      node_type: nodeType,
      layout_variant: layoutVariant,
      action_count: actionIds.length,
      action_ids: { ids: actionIds },
      visible_action_order: { ids: actionIds },
      rendered_controls: { controls: actions.map((action) => ({
        action_id: action.key,
        label: action.label,
        presentation: action.variant === 'primary' ? 'icon-and-label' : 'icon-only',
      })) },
      outer_container_count: 1,
      control_borders_visible: false,
      hover_surface_shape: 'rounded',
      dividers_visible: false,
      requested: `visible_action_order=${traceActionNames.join(' > ')}`,
      resolved: `variant=${layoutVariant}; outer=1; borders=false; hover=rounded; dividers=false`,
    })
  }, [actions, nodeId, nodeType, projectId])

  return (
    <motion.div
      initial={{ opacity: 0, y: 10, scale, x: inline ? 0 : '-50%' }}
      animate={{ opacity: 1, y: 0, scale, x: inline ? 0 : '-50%' }}
      exit={{ opacity: 0, y: 10, scale, x: inline ? 0 : '-50%' }}
      transition={{
        type: 'spring',
        stiffness: 400,
        damping: 25,
        opacity: { duration: 0.15 },
      }}
      className={cn('z-[60] nopan nodrag', inline ? 'relative mb-6 flex justify-center' : 'absolute')}
      style={
        inline
          ? undefined
          : {
              bottom: '100%',
              left: '50%',
              marginBottom: FLOATING_NODE_TOOLBAR_LABEL_CLEARANCE_PX,
              transformOrigin: 'bottom center',
            }
      }
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      data-nux-anchor="selected-node-toolbar"
      data-nux-node-id={nodeId ?? undefined}
      data-nux-node-type={nodeType}
    >
      <div
        data-testid="agent-node-toolbar"
        className={cn('flex items-center gap-2 rounded-full border border-border bg-surface-2 px-2 py-1 shadow-xl backdrop-blur-md', inline && 'flex-wrap justify-center')}
      >
        {actions.map((action) => (
          <div key={action.key} className="flex items-center">
            <ToolbarActionControl action={action} />
          </div>
        ))}
      </div>
    </motion.div>
  )
})

const ToolbarActionControl = memo(function ToolbarActionControl({
  action,
}: {
  action: ToolbarAction
}) {
  // canvas_node_toolbar_action context (only mapped tool keys emit; see interactionAnalytics).
  const analyticsNodeId = useNodeId()
  const analyticsProjectId = useOptionalProjectId()
  const analyticsPresentation = useNodePresentation()
  const analyticsNodeType = useStore((s) => (analyticsNodeId ? s.nodeInternals.get(analyticsNodeId)?.type : undefined))
  const analyticsPickedIndex = useStore((s) => (analyticsNodeId ? pickedOutputIndex(s.nodeInternals.get(analyticsNodeId)?.data) : undefined))
  const { emitNuxEvent } = useNuxTooltipEvents()
  const trackToolbarKey = (key: string) => trackNodeToolbarAction({
    projectId: analyticsProjectId,
    nodeId: analyticsNodeId,
    nodeType: analyticsNodeType,
    key,
    viewMode: viewModeForPresentation(analyticsPresentation),
    pickedIndex: analyticsPickedIndex,
  })
  const [menuOpen, setMenuOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  // True while the open menu was opened by hover (not click/keyboard), so a
  // click on the hovered trigger keeps it open instead of toggling it shut.
  const hoverOpenedRef = useRef(false)
  const hoverCloseTimerRef = useRef<number | null>(null)
  const pointerInsideRef = useRef(false)
  const visibleMenuItems =
    action.menuItems?.filter((item) => !item.hidden) ?? []
  const isMenu = action.menuItems !== undefined
  const disabled =
    action.disabled ||
    (isMenu && visibleMenuItems.every((item) => item.disabled))
  const tooltipId = useId()
  const disabledReason =
    disabled && action.disabledReason ? action.disabledReason : undefined
  // A disabled action that explains itself stays focusable (aria-disabled
  // instead of the native attribute) so keyboard users can reach the reason;
  // the click handler below refuses to act while disabled.
  const nativeDisabled = disabled && !disabledReason

  const clearHoverCloseTimer = useCallback(() => {
    if (hoverCloseTimerRef.current !== null) {
      window.clearTimeout(hoverCloseTimerRef.current)
      hoverCloseTimerRef.current = null
    }
  }, [])

  useEffect(() => clearHoverCloseTimer, [clearHoverCloseTimer])

  useEffect(() => {
    if (!menuOpen) hoverOpenedRef.current = false
  }, [menuOpen])

  const handleMouseEnter = () => {
    pointerInsideRef.current = true
    if (!isMenu || disabled) return
    clearHoverCloseTimer()
    if (!menuOpen) {
      hoverOpenedRef.current = true
      setMenuOpen(true)
      toolbarMenuLog({ event: 'toolbar-menu:open', action: action.key, trigger: 'hover' })
    }
  }

  // Hover content must stay available while keyboard focus is inside it
  // (WCAG SCR39): a user who hovered open the menu and tabbed into an item
  // keeps it open after the pointer leaves. Mouse clicks also focus the
  // trigger, so only keyboard (:focus-visible) focus pins the menu open.
  const hasKeyboardFocusWithin = () => {
    const active = document.activeElement
    if (!(active instanceof HTMLElement) || !containerRef.current?.contains(active)) return false
    try {
      return active.matches(':focus-visible')
    } catch {
      return true
    }
  }

  const handleMouseLeave = () => {
    pointerInsideRef.current = false
    if (!isMenu || !menuOpen) return
    clearHoverCloseTimer()
    hoverCloseTimerRef.current = window.setTimeout(() => {
      hoverCloseTimerRef.current = null
      if (hasKeyboardFocusWithin()) {
        toolbarMenuLog({ event: 'toolbar-menu:keep-open', action: action.key, reason: 'keyboard-focus-within' })
        return
      }
      setMenuOpen(false)
      toolbarMenuLog({ event: 'toolbar-menu:close', action: action.key, trigger: 'hover-leave' })
    }, TOOLBAR_MENU_HOVER_CLOSE_DELAY_MS)
  }

  const handleFocus = () => {
    clearHoverCloseTimer()
  }

  const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
    const next = event.relatedTarget
    if (next instanceof Node && containerRef.current?.contains(next)) return
    // Focus left a hover-opened menu while the pointer is also outside: the
    // hover close was deferred for the keyboard user, so close now.
    if (menuOpen && hoverOpenedRef.current && !pointerInsideRef.current) {
      setMenuOpen(false)
      toolbarMenuLog({ event: 'toolbar-menu:close', action: action.key, trigger: 'focus-leave' })
    }
  }

  useEffect(() => {
    if (!menuOpen) return

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target
      if (!(target instanceof Node) || !containerRef.current?.contains(target)) {
        setMenuOpen(false)
      }
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false)
    }

    document.addEventListener('pointerdown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [menuOpen])

  return (
    <div
      ref={containerRef}
      className="relative group"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
      onBlur={handleBlur}
      data-nux-anchor={action.nuxAnchorId}
      data-nux-control-id={action.nuxControlId}
      data-nux-node-id={analyticsNodeId ?? undefined}
      data-nux-node-type={analyticsNodeType}
    >
      <button
        ref={action.triggerRef}
        aria-label={action.label}
        aria-haspopup={action.ariaHasPopup ?? (isMenu ? 'menu' : undefined)}
        aria-expanded={action.ariaExpanded ?? (isMenu ? menuOpen : undefined)}
        aria-controls={action.ariaControls}
        aria-describedby={disabledReason ? tooltipId : undefined}
        aria-disabled={disabledReason ? true : undefined}
        onClick={(event) => {
          event.stopPropagation()
          if (disabled) return
          if (isMenu) {
            if (hoverOpenedRef.current) {
              // Already revealed by hover: treat the click as "keep open".
              hoverOpenedRef.current = false
              clearHoverCloseTimer()
              setMenuOpen(true)
            } else {
              setMenuOpen((open) => !open)
            }
          } else {
            trackToolbarKey(action.key)
            if (action.nuxControlId) {
              emitNuxEvent({
                type: 'controlActivated',
                controlId: action.nuxControlId,
                anchorId: action.nuxAnchorId,
                nodeId: analyticsNodeId ?? undefined,
                nodeType: analyticsNodeType,
              })
            }
            action.onClick()
          }
        }}
        disabled={nativeDisabled}
        className={cn(
          'flex h-8 min-w-8 items-center justify-center gap-0.5 rounded-lg px-2 text-xs font-medium whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20',
          action.variant === 'primary' &&
            'bg-primary/90 text-primary-foreground hover:bg-primary',
          action.variant === 'danger' &&
            'text-text-muted hover:bg-destructive/10 hover:text-destructive',
          action.variant !== 'primary' &&
            action.variant !== 'danger' &&
            'text-text-muted hover:bg-surface-1 hover:text-foreground',
          disabled && 'opacity-40 cursor-not-allowed',
          menuOpen && 'bg-surface-1 text-foreground',
        )}
      >
        <span className="w-4 h-4 flex items-center justify-center">
          {action.icon}
        </span>
        {isMenu && <ChevronDown className="h-3 w-3" aria-hidden="true" />}
        {action.variant === 'primary' && <span>{action.label}</span>}
      </button>

      {(action.variant !== 'primary' || disabledReason) && !menuOpen && (
        <div
          role={disabledReason ? 'tooltip' : undefined}
          data-testid={`toolbar-tooltip-${action.key}`}
          className={cn(
            'absolute left-1/2 -translate-x-1/2 bottom-full mb-1.5 px-2 py-1 text-xs text-foreground bg-surface-2 border border-border rounded shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-100',
            disabledReason
              ? 'w-max max-w-56 text-center group-has-[:focus-visible]:opacity-100'
              : 'whitespace-nowrap',
          )}
        >
          {/* Primary actions already render their label inline. */}
          {action.variant !== 'primary' && <span className="block">{action.label}</span>}
          {disabledReason && (
            <span id={tooltipId} className="block text-text-muted">
              {disabledReason}
            </span>
          )}
        </div>
      )}

      <AnimatePresence>
        {isMenu && menuOpen && (
          <motion.div
            role="menu"
            aria-label={action.label}
            initial={{ opacity: 0, x: '-50%', y: -4, scale: 0.98 }}
            animate={{ opacity: 1, x: '-50%', y: 0, scale: 1 }}
            exit={{ opacity: 0, x: '-50%', y: -4, scale: 0.98 }}
            transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="absolute left-1/2 top-full z-50 mt-2 min-w-36 rounded-lg border border-border bg-background p-1 text-left shadow-xl before:absolute before:inset-x-0 before:-top-2 before:h-2 before:content-['']"
          >
            {visibleMenuItems.map((item) => (
              <button
                key={item.key}
                role="menuitem"
                type="button"
                disabled={item.disabled}
                onClick={(event) => {
                  event.stopPropagation()
                  setMenuOpen(false)
                  trackToolbarKey(item.key)
                  item.onClick()
                }}
                className={cn(
                  'flex w-full items-center justify-start gap-2 rounded-md border border-transparent px-3 py-2 text-left text-sm text-foreground transition-colors',
                  'hover:border-input-border-hover hover:bg-surface-2',
                  'focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20',
                  item.disabled && 'cursor-not-allowed opacity-40',
                )}
              >
                {item.icon && (
                  <span className="flex h-4 w-4 shrink-0 items-center justify-center text-muted-foreground">
                    {item.icon}
                  </span>
                )}
                <span className="min-w-0 flex-1 truncate text-left">{item.label}</span>
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
})

ToolbarActionControl.displayName = 'ToolbarActionControl'

VisibleAgentNodeToolbar.displayName = 'VisibleAgentNodeToolbar'

AgentNodeToolbar.displayName = 'AgentNodeToolbar'
